import { IconFile, IconFolder, IconFolderOpen } from '@tabler/icons-react';

import { useParams } from 'react-router-dom';

import {
    Box,
    Collapse,
    Group,
    MantineStyleProp,
    Tooltip,
    UnstyledButton
} from '@mantine/core';

import { DocumentDto, ProjectDto } from '../../api/models';
import DocumentMenu from '../Documents/DocumentMenu.tsx';
import ProjectMenu from '../Projects/ProjectMenu.tsx';
import DevOpsFileTree from './DevOpsFileTree.tsx';
import { NavLinksGroupItem } from './DocumentOverview.tsx';

interface NavLinksGroupProps extends NavLinksGroupItem {
    refetch: () => Promise<ProjectDto[]>;
    opened: boolean;
    toggleOpened: () => void;
    onProjectArchived: (project: ProjectDto) => void;
    onDocumentCreated: (document: DocumentDto) => void;
    onDocumentDeleted: (document: DocumentDto) => void;
    onDocumentSelected: (document: DocumentDto, projectName: string) => void;
    onShowAddDocument: (project: ProjectDto) => void;
    onDevOpsFileSelected: (filePath: string) => void;
}

const NavLinksGroup = ({
    label,
    documents,
    project,
    client,
    refetch,
    opened,
    toggleOpened,
    onDocumentCreated,
    onDocumentDeleted,
    onDocumentSelected,
    onProjectArchived,
    onShowAddDocument,
    onDevOpsFileSelected
}: NavLinksGroupProps) => {
    const { documentId } = useParams();
    const hasDocuments = Array.isArray(documents) && documents.length > 0;
    const hasContent = hasDocuments || !!project.devOpsRepoId;

    const handleProjectClick = () => {
        if (hasContent) {
            toggleOpened();
        } else {
            onShowAddDocument(project);
        }
    };

    const containerStyle: MantineStyleProp = {
        fontWeight: '500',
        display: 'block',
        flex: 1,
        minWidth: 0,
        padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
        color: 'var(--mantine-color-text)',
        fontSize: 'var(--mantine-font-size-sm)'
    };

    const documentStyle: MantineStyleProp = {
        ...containerStyle,
        flex: 1,
        minWidth: 0,
        textDecoration: 'none',
        paddingLeft: 'var(--mantine-spacing-md)',
        marginLeft: 'var(--mantine-spacing-xl)',
        borderLeft:
            '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        display: 'flex',
        alignItems: 'center'
    };

    const items = (hasDocuments ? documents : []).map(document => {
        const isSelected = documentId === document.id;

        return (
            <div key={document.id}>
                <Group justify='space-between' gap={0}>
                    <Tooltip label={document.title}>
                        <UnstyledButton
                            style={{
                                ...documentStyle,
                                display: 'flex',
                                alignItems: 'center',
                                ...(isSelected ? {
                                    color: 'var(--mantine-color-brand-5)',
                                    fontWeight: 500,
                                    background: 'var(--mantine-color-brand-light)',
                                    borderRadius: 'var(--mantine-radius-sm)'
                                } : {})
                            }}
                            onClick={() =>
                                onDocumentSelected(document, project.name)
                            }
                        >
                            <Box
                                component={IconFile}
                                size={20}
                                style={{ flexShrink: 0, color: isSelected ? 'var(--mantine-color-brand-5)' : 'var(--mantine-color-gray-6)' }}
                            />
                            <Box ml='md' style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{document.name}</Box>
                        </UnstyledButton>
                    </Tooltip>
                    <DocumentMenu
                        document={document}
                        project={project}
                        refetch={refetch}
                        onDocumentDeleted={onDocumentDeleted}
                        menuColor='gray'
                    />
                </Group>
            </div>
        );
    });

    return (
        <div>
            <Group justify='space-between' gap={0}>
                <Tooltip
                    label={`${client?.name}${project?.code ? ` (${project.code})` : ''}`}
                >
                    <UnstyledButton
                        variant='transparent'
                        onClick={handleProjectClick}
                        style={containerStyle}
                    >
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                component={opened ? IconFolderOpen : IconFolder}
                                size={20}
                                style={{ flexShrink: 0, color: 'var(--mantine-color-gray-6)' }}
                            />
                            <Box ml='md' style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {label}
                                {project?.code && (
                                    <Box
                                        component='span'
                                        c='dimmed'
                                        fz='xs'
                                        ml='xs'
                                    >
                                        ({project.code})
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </UnstyledButton>
                </Tooltip>
                <ProjectMenu
                    project={project}
                    refetch={refetch}
                    onDocumentCreated={onDocumentCreated}
                    onProjectArchived={onProjectArchived}
                    menuColor='gray'
                />
            </Group>

            <Collapse in={opened}>
                {hasDocuments && project.devOpsRepoId && (
                    <Box fz="xs" c="dimmed" ml="xl" pl="md" mt={4} mb={2}>
                        Project Documents
                    </Box>
                )}
                {hasDocuments && items}
                {project.devOpsRepoId && (
                    <DevOpsFileTree project={project} onFileSelected={onDevOpsFileSelected} documentStyle={documentStyle} />
                )}
            </Collapse>
        </div>
    );
};

export default NavLinksGroup;
